import { Bank } from '@huspy/briks-icons';
import { useTranslation } from 'react-i18next';
import {
  Box, Flex, Input,
  InputLabel,
  Text
} from '@huspy/briks-web';
import { SectionHeader } from '@modules/opportunities/presentation/pages/CaseCreation/components/CaseInputs';
import {
  FormInput,
  FormSelectInput,
} from '@huspy/forge';
import {
  getApplicationType,
  getTransactionType,
  getStatusOfProperty,
  getStates,
  getMortgageType,
  getYesNo,
} from '@modules/opportunities/presentation/pages/const';
import { OPPORTUNITY_CURRENCY } from '@modules/opportunities/entities/opportunity/const';
import { UseFormReturnType } from '@mantine/form';
import CaseCreationBasicInfoBanksSection from '@modules/opportunities/presentation/pages/CaseCreation/views/CaseBasicInfo/components/CaseCreationBasicInfoBanksSection';
import { BANK_APPLICATION_STATUS } from '@modules/opportunities/entities/bankApplication/const';
import { BankApplication } from '@modules/opportunities/entities/bankApplication/bankApplication.entity';
import { BusinessIdentifier } from '@modules/core/api/types';
import { BUSINESS_IDENTIFIER } from '@modules/core/api/const';
import Radio from '@components/v2/Radio';
import { useState } from 'react';

type MortgageInfoProps = {
  form: UseFormReturnType<any>;
  isSomeCasesSubmitted: boolean;
  bankApplications: BankApplication[];
  hash: string;
  businessUnit: BusinessIdentifier;
  initialLoanToValue?: number;
};

export const MortgageInfo = ({
  form,
  bankApplications,
  isSomeCasesSubmitted,
  businessUnit,
  hash,
  initialLoanToValue = 0,
}: MortgageInfoProps) => {
  const { t } = useTranslation();
  const isSpain = businessUnit === BUSINESS_IDENTIFIER.ES_BAYTECA;
  const isUAE = businessUnit === BUSINESS_IDENTIFIER.AE_HUSPY;
  const [isPropertyIdentified, setIsPropertyIdentified] = useState(
    form.getValues().is_property_identified
  );

  const [loanToValue, setLoanToValue] = useState(initialLoanToValue);

  form.watch('property_value', ({ value }: { value: number }) => {
    if (isSpain) {
      const loanAmount = form.getValues().amount;
      setLoanToValue((loanAmount / value) * 100);
    }
  });

  form.watch('amount', ({ value }: { value: number }) => {
    if (isSpain) {
      const propertyValue = form.getValues().property_value;
      setLoanToValue((value / propertyValue) * 100);
    }
  });

  return (
    <>
      <SectionHeader
        title={ t('clients.formSections.mortgageInfo') }
        Icon={ Bank }
      />
      {isUAE && (
        <CaseCreationBasicInfoBanksSection
          onChange={ (selected_banks) => {
            form.setFieldValue('selected_banks', selected_banks ?? []);
          } }
          error={ form.getInputProps('selected_banks')?.error }
          defaultValue={ form.getValues().selected_banks }
          isOpen={ hash !== '' }
          banks={ form.getValues().selected_banks.map((bank: any, i: number) => {
            const bankApplicationStatus = bankApplications.find(
              (ba) => ba.bank_details.external_id === bank.id
            )?.status;
            return {
              error: form.getInputProps(`selected_banks.${i}.mortgageType`)
                .error,
              icon: bank.icon,
              name: bank.name,
              mortgageType: bank.mortgageType!,
              id: bank.id,
              isSubmitted: bankApplicationStatus
                ? bankApplicationStatus !== BANK_APPLICATION_STATUS.draft
                : false,
            };
          }) }
          onMortgageTypeChange={ (idx, value) => {
            form.setFieldValue(`selected_banks.${idx}.mortgageType`, value);
          } }
        />
      )}
      {isSpain && (
        <Box id='selected_banks' data-test='select-banks'>
          <CaseCreationBasicInfoBanksSection
            onChange={ (selected_banks) => {
              form.setFieldValue('selected_banks', selected_banks ?? []);
            } }
            error={ form.getInputProps('selected_banks')?.error }
            defaultValue={ form.getValues().selected_banks }
            isOpen={ hash !== '' }
            defaultMortgageType={ form.getValues().mortgage_type }
          />
        </Box>
      )}
      <Flex gap='4'>
        <FormSelectInput
          name='application_type'
          label={ t('clients.formLabels.applicationType') }
          form={ form }
          data={ getApplicationType() }
          disabled={ isSomeCasesSubmitted }
          data-test='application-type'
        />
        <FormSelectInput
          name='state'
          label={ t('clients.formLabels.province') }
          form={ form }
          data={ getStates() }
          disabled={ isSomeCasesSubmitted }
        />
      </Flex>
      <Flex gap='4'>
        <FormInput
          name='amount'
          label={ t('clients.formLabels.loanAmount') }
          data-test='loan-amount'
          form={ form }
          type='number'
          trail={ isSpain ? OPPORTUNITY_CURRENCY.EUR : OPPORTUNITY_CURRENCY.AED }
          disabled={ isSomeCasesSubmitted }
        />
        <FormSelectInput
          name='type_of_transaction'
          label={ t('clients.formLabels.transactionType') }
          form={ form }
          data={ getTransactionType() }
          disabled={ isSomeCasesSubmitted }
        />
      </Flex>
      <Flex gap='4'>
        <Flex
          gap='4'
          width='50%'
          align={ form.errors?.mortgage_years || form.errors?.mortgage_months
            ? 'start'
            : 'end' }
          justify='center'
        >
          <FormInput
            name='mortgage_years'
            label={ t('clients.formLabels.mortgageTerm') }
            data-test='mortgage-term-years'
            form={ form }
            trail={ t('common.forms.mortgageTerm.placeholder') }
            type='number'
            disabled={ isSomeCasesSubmitted }
          />
          <FormInput
            name='mortgage_months'
            placeholder='10'
            trail={ t('common.forms.mortgageTermMonths.placeholder') }
            data-test='mortgage-term-months'
            form={ form }
            type='number'
            disabled={ isSomeCasesSubmitted }
          />
        </Flex>
        <FormInput
          name='property_value'
          label={ t('clients.formLabels.estimatedPropertyValue') }
          data-test='property-value'
          form={ form }
          type='number'
          trail={ isSpain ? OPPORTUNITY_CURRENCY.EUR : OPPORTUNITY_CURRENCY.AED }
          disabled={ isSomeCasesSubmitted }
        />
      </Flex>
      <Flex gap='4'>
        {isSpain && (
          <FormSelectInput
            name='mortgage_type'
            data-test='select-mortgage-type'
            label={ t('clients.formLabels.mortgageType') }
            form={ form }
            data={ getMortgageType() }
            disabled={ isSomeCasesSubmitted }
          />
        )}
        <FormSelectInput
          name='property_status'
          label={ t('clients.formLabels.propertyStatus') }
          form={ form }
          data={ getStatusOfProperty() }
          disabled={ isSomeCasesSubmitted }
        />
      </Flex>
      {isSpain && (
        <Flex gap='4'>
          <Box w='50%'>
            <InputLabel>
              {t('clients.formLabels.loanToValue')}
            </InputLabel>
            <Input
              type='number'
              value={ loanToValue }
              disabled
              icon={ (
                <Text size='md' color='neutral.400'>
                  %
                </Text>
              ) }
            />
          </Box>
          <Box id='is_property_identified' w='50%'>
            <Radio
              name='is_property_identified'
              data-test='select-property-identified'
              label={ t('clients.formLabels.propertyIdentified') }
              data={ getYesNo() }
              defaultValue={ isPropertyIdentified ? 'yes' : 'no' }
              onValueChange={ ({ value }) => {
                form.setFieldValue('is_property_identified', value === 'yes');
                setIsPropertyIdentified(value === 'yes');
              } }
              disabled={ isSomeCasesSubmitted }
            />
          </Box>
          {isPropertyIdentified && (
            <Box id='is_property_mou_signed' w='50%'>
              <Radio
                name='is_property_mou_signed'
                data-test='property-mou-signed'
                label={ t('clients.formLabels.hasSignedArras') }
                data={ getYesNo() }
                defaultValue={ form.getValues().is_property_mou_signed ? 'yes' : 'no' }
                onValueChange={ ({ value }) => {
                  form.setFieldValue('is_property_mou_signed', value === 'yes');
                } }
                disabled={ isSomeCasesSubmitted }
              />
            </Box>
          )}
        </Flex>
      )}
    </>
  );
};
